import { AliffExportTimeErrorCode, AliffRuleType, AliffShapeType, DEFAULT_NET_PREFERENCE_CONFIGS, DEFAULT_THROUGH_HOLE_VIA_UID, FootPrintPadHoleType, LayerOrientation, PcbNodeTypes, PrimitiveRole, PrimitiveType, createNetNode, createNode, createRouteSegmentNode, getLayerIdentifier, getOrderedCopperLayers, } from "@buildwithflux/models";
import { guid, milsToMeters } from "@buildwithflux/shared";
import { pick, pickBy } from "lodash";
import { Vector3 } from "three";
import { Clipper } from "../../../clipper";
import { vec2 } from "../../../common";
import { CurveDivisions, calcKeepoutForNodes, createPolygonShape } from "../../../fills";
import { PcbNodeHelper, getAllLeafElementNodes, getDescendantsOfType } from "../../../nodes";
import { PcbSelectorMatcher, getConnectedCopperLayers, getNodeAbsVertexPosition } from "../../../pcb/layout_engine";
import { TraceCorners, createBakedPseudoNode } from "../../../pcb/libs";
import { getMultiRoutingGroups } from "../../../pcb/multirouting";
import { approxFittingPolygon } from "../../../pcb/primitive_store";
import { createLayoutShape } from "../../../shapes/primitives";
import { getEnrichedViaOptions, getStackupLayersAndViaConfigs, getViaConfigConnectedLayers, getViaSegmentFromType, getViaSegmentResolvedSizeValues, isThroughHoleViaConfig, } from "../../../smart_via";
import { BaseExporter } from "../BaseExporter";
import { AliffPriorityMapper } from "./AliffPriorityMapper";
import { AliffHelpers } from "./helpers";
/**
 * Aliff: Auto Layout Intermediate File Format
 *
 * This file defines the intermediate file format for the auto-layout tool.
 * We are using the schema provided by DeepPCB as our own Aliff as well.
 * In the future, if and when we start using different auto-layout services,
 * we can use Aliff as a common format to convert between different formats.
 */
export class AliffExporter extends BaseExporter {
    layoutNode;
    jobConfig;
    services;
    static DEFAULT_NET_CLASS_ID = "__default__";
    static UNCONNECTED_NET_ID = "UNCONNECTED_NET";
    static DEFAULT_NET_CLEARANCE_VALUE = AliffHelpers.toNm(milsToMeters(5)); // 5mils
    // TODO: refactor this to be global
    static POLYGON_CURVE_DIVISION = 64;
    __debug = false;
    activePcbLayoutNodes;
    activePcbLayoutNodesWithPseudoNetNode;
    copperLayers;
    nodeShapesMap;
    holeTypePadNodes = [];
    pcbSelectorMatcher;
    pseudoBakedNetNode;
    createdElementsCache = new Set();
    createdPinsCache = new Set();
    padStacks = {};
    viaDefinitions = [];
    netNodeToAliffNetIdMap = {};
    objectIdToNodeUidMap = {};
    exportTimeErrors = [];
    constructor(userData, documentData, layoutNode, jobConfig, services) {
        super(documentData, userData);
        this.layoutNode = layoutNode;
        this.jobConfig = jobConfig;
        this.services = services;
        const { pcbLayoutNodes } = this.services.documentService.snapshot();
        this.activePcbLayoutNodes = pickBy(pcbLayoutNodes, (node) => node.bakedRules.active);
        this.copperLayers = getOrderedCopperLayers(this.layoutNode.bakedRules.stackup);
        this.nodeShapesMap = services.pcbPrimitiveStore.getAllNodePrimitivesMap();
        this.pcbSelectorMatcher = new PcbSelectorMatcher(this.documentData.elements, this.documentData.nets, pcbLayoutNodes, pick(this.documentData, "uid", "name", "description"), services.logger);
        this.pseudoBakedNetNode = this.createPseudoBakedNetNode();
        this.activePcbLayoutNodesWithPseudoNetNode = {
            ...this.activePcbLayoutNodes,
            [this.pseudoBakedNetNode.uid]: this.pseudoBakedNetNode,
        };
    }
    /**
     * Download the PCB in DeepPCB format with the supplied extension
     */
    download(extension, downloadFn) {
        const board = this.generateBoard();
        const boardString = JSON.stringify(board, null, 2);
        if (this.__debug) {
            this.services.logger.debug(boardString);
        }
        else {
            downloadFn(boardString, this.createFileName(extension));
        }
    }
    /**
     * Generates and returns a JSON of the PCB in DeepPCB format
     */
    generateBoard() {
        const name = this.generateName();
        const resolution = this.generateResolution();
        // Generate the board boundary and planes
        const boundary = this.generateBoardBoundary();
        const { planes, nodesWithFills } = this.generatePlanes(boundary);
        // Generate the components and their definitions
        // This will also populate the pad stacks with component pads' pad stacks
        const { componentDefinitions, components } = this.generateComponents();
        // Create pad stacks for all supported via configs defined in the layout stackup
        // This will populate the this.viaDefinitions and this.padStacks class properties
        // viaDefinition == padstackId == viaConfigUid
        const { throughHoleViaConfigUids } = this.generatePadStacksForAllViaConfigs();
        // Generate the nets and net classes
        const netsMap = this.generateNets();
        const nets = Object.values(netsMap);
        const { netClasses, netUidToNetClassMap } = this.generateNetClasses(netsMap, throughHoleViaConfigUids);
        // Generate net preferences
        const netPreferences = this.generateNetPreferences(netsMap);
        // Generate the existing vias, and via definitions from the stackup
        // This will also populate the pad stacks with via definition pad stacks
        const vias = this.generateVias();
        // Generate the layers as well as keepouts for zones and board holes
        // Make sure to generate keepouts _after_ generating components and vias,
        // since the above methods will populate the `holeTypePadNodes` array which is used
        // to create keepouts for hole pads
        const layers = this.generateLayers();
        this.generateKeepouts(layers, nodesWithFills);
        // Generate differential pairs
        const differentialPairs = this.generateDifferentialPairs(netsMap, netUidToNetClassMap);
        // Generate the rules
        // We need nets and nodesWithFills to generate clearance rules
        const rules = this.generateRules(nets, nodesWithFills);
        // Once all pad stacks are generated by the methods above, create the list of pad stacks
        const padstacks = Object.values(this.padStacks);
        // Generate the wires (traces)
        const wires = this.generateWires();
        // Finally, add userdata to the board boundary after we have all the necessary data
        this.updateBoardBoundaryUserData(boundary);
        return {
            name,
            rules,
            resolution,
            boundary,
            componentDefinitions,
            components,
            layers,
            netClasses,
            nets,
            netPreferences,
            differentialPairs,
            padstacks,
            planes,
            viaDefinitions: this.viaDefinitions,
            vias,
            wires,
        };
    }
    getBoardExportMetadata() {
        return {
            objectIdToNodeUidMap: this.objectIdToNodeUidMap,
            exportTimeErrors: this.exportTimeErrors,
        };
    }
    /** Generates a file name for the DeepPCB file */
    generateName() {
        return this.documentData.name;
    }
    /** Generate the rules for the DeepPCB board */
    generateRules(nets, nodesWithFills) {
        const rules = [
            { type: AliffRuleType.allowViaAtSmd, value: true },
            { type: AliffRuleType.rotateFirst, value: false },
        ];
        if (this.jobConfig.version === "v1") {
            rules.push({ type: AliffRuleType.pinConnectionPoint, value: "position" });
        }
        // Create a clearance value for every fill net node with every other net node
        for (const nodeWithFill of nodesWithFills) {
            const fillNetUid = nodeWithFill.type === PcbNodeTypes.net ? nodeWithFill.uid : nodeWithFill.bakedRules.effectiveNetId;
            for (const net of nets) {
                if (net.id === this.getAliffNetId(fillNetUid)) {
                    continue;
                }
                const netNodeUid = this.objectIdToNodeUidMap[net.id];
                if (!netNodeUid) {
                    continue;
                }
                const netNode = this.getBakedNode(netNodeUid, PcbNodeTypes.net);
                if (!netNode) {
                    continue;
                }
                // The keepout value between the fill net node and every other net node
                const keepout = AliffHelpers.toNm(calcKeepoutForNodes(nodeWithFill, netNode, this.pcbSelectorMatcher));
                const clearance = keepout === 0 || keepout === Infinity ? AliffExporter.DEFAULT_NET_CLEARANCE_VALUE : keepout;
                const clearanceRule = {
                    subjects: [
                        // If the node is a net node, get its aliff net ID, otherwise use its UID
                        { id: this.netNodeToAliffNetIdMap[nodeWithFill.uid] ?? nodeWithFill.uid, type: "netClass" },
                        { id: net.id, type: "netClass" },
                    ],
                    type: AliffRuleType.clearance,
                    value: clearance,
                };
                rules.push(clearanceRule);
            }
        }
        return rules;
    }
    /**
     * Generate the resolution for the DeepPCB board
     * Current resolution: 1nm (1{unit}/{value})
     * This resolution should be smaller than our connectivityTolerance (200nm)
     * to prevent airwires from appearing.
     * */
    generateResolution() {
        return {
            unit: "um",
            value: 1000,
        };
    }
    generateBoardBoundary() {
        const shape = createLayoutShape(this.layoutNode.bakedRules);
        if (!shape) {
            throw new Error("Could not create shape for layout node");
        }
        const points = shape
            .getPoints(AliffExporter.POLYGON_CURVE_DIVISION)
            .map((point) => AliffHelpers.getPoint(point));
        return {
            // This will be updated by `generatePlanes` to use the max clearance from the layout edge
            clearance: 0,
            shape: {
                type: AliffShapeType.polyline,
                points,
            },
            // This will be updated by `updateBoardBoundaryUserData()`
            userData: undefined,
        };
    }
    updateBoardBoundaryUserData(boardBoundary) {
        const userData = {
            layoutUid: this.layoutNode.uid,
            objectIdToNodeUidMap: this.objectIdToNodeUidMap,
        };
        boardBoundary.userData = JSON.stringify(userData);
    }
    generatePolygonBoundary(polygonNode) {
        const shape = createPolygonShape(polygonNode);
        if (!shape) {
            throw new Error("Could not create shape for polygon node");
        }
        const points = shape
            .getPoints(AliffExporter.POLYGON_CURVE_DIVISION)
            .map((point) => AliffHelpers.getPoint(point));
        return {
            // This will be updated by `generatePlanes` to use the max clearance from the layout edge
            clearance: 0,
            shape: {
                type: AliffShapeType.polyline,
                points,
            },
        };
    }
    getLayerOrder(layer) {
        const layerId = typeof layer === "string" ? layer : getLayerIdentifier(layer);
        return this.copperLayers.findIndex((copperLayer) => getLayerIdentifier(copperLayer) === layerId);
    }
    generateKeepouts(layers, nodesWithFills) {
        // DeepPCB doesn't support "Everything" type right now
        const keepoutType = ["Wire", "Via"];
        // Create a cache of copperLayer->connectedNodeWithFill to avoid recalculating every time
        const copperLayersToConnectedNodeWithFillMap = nodesWithFills.reduce((acc, nodeWithFill) => {
            const netConnectedLayers = getConnectedCopperLayers(nodeWithFill, this.copperLayers);
            for (const connectedLayer of netConnectedLayers) {
                const layerOrder = this.getLayerOrder(connectedLayer);
                const connectedNodeWithFill = acc[layerOrder] ?? [];
                connectedNodeWithFill.push(nodeWithFill);
                acc[layerOrder] = connectedNodeWithFill;
            }
            return acc;
        }, {});
        // 1. Generate keepouts for zone nodes
        const zoneNodes = this.getNodesOfType(PcbNodeTypes.zone);
        for (const zoneNode of zoneNodes) {
            const zoneShapeData = this.nodeShapesMap.get(zoneNode.uid);
            if (!zoneShapeData)
                continue;
            // Zones always only have one shape
            const [zoneShape] = zoneShapeData.primitives;
            if (!zoneShape)
                continue;
            // We want to use polygon instead of DeepPCB's "circle" or "rectangle"
            // primitives to ensure that scale, rotation, or any other mutation is respected
            // Using the PCBShapeStore provides this.
            const zoneConnectedLayers = zoneNode.bakedRules.connectedLayers ?? ["All"];
            const zoneLayerOrders = this.copperLayers.reduce((acc, layer, index) => {
                const layerId = getLayerIdentifier(layer);
                if (zoneConnectedLayers.includes(layerId) || zoneConnectedLayers.includes("All")) {
                    acc.push(index);
                }
                return acc;
            }, []);
            if (zoneLayerOrders.length === 0)
                continue;
            for (const layerOrder of zoneLayerOrders) {
                // Compute the keepout by which we should expand the zone shape
                const connectedNodeWithFill = copperLayersToConnectedNodeWithFillMap[layerOrder] ?? [];
                const maxKeepoutFromAnyFillNet = connectedNodeWithFill.reduce((acc, nodeWithFill) => {
                    const keepout = calcKeepoutForNodes(zoneNode, nodeWithFill, this.pcbSelectorMatcher);
                    return Math.max(acc, keepout);
                }, 0);
                const expandedZonePolygon = AliffHelpers.expandShape(zoneShape, maxKeepoutFromAnyFillNet);
                layers[layerOrder]?.keepouts.push({
                    type: keepoutType,
                    layer: layerOrder,
                    shape: {
                        type: AliffShapeType.polygon,
                        points: expandedZonePolygon.map((point) => AliffHelpers.getPoint(point)),
                    },
                });
            }
        }
        // 2. Generate keepouts for board holes
        // Hole goes through all layers
        for (const holePadNode of this.holeTypePadNodes) {
            const nodeShapes = this.nodeShapesMap.get(holePadNode.uid);
            if (!nodeShapes) {
                continue;
            }
            const primitives = nodeShapes.primitives;
            const holeShapes = primitives.filter((shape) => shape.role === PrimitiveRole.layoutHole);
            if (holeShapes.length === 0) {
                continue;
            }
            // Hole primitives go through all the layers
            for (const copperLayer of this.copperLayers) {
                // Compute the keepout by which we should expand the hole shape
                const layerOrder = this.getLayerOrder(copperLayer);
                const connectedNodeWithFill = copperLayersToConnectedNodeWithFillMap[layerOrder] ?? [];
                const maxKeepoutFromAnyFillNet = connectedNodeWithFill.reduce((acc, nodeWithFill) => {
                    const keepout = calcKeepoutForNodes(holePadNode, nodeWithFill, this.pcbSelectorMatcher);
                    return Math.max(acc, keepout);
                }, 0);
                const expandedHoleShapes = holeShapes.map((holeShape) => {
                    const expandedHolePolygon = AliffHelpers.expandShape(holeShape, maxKeepoutFromAnyFillNet);
                    return {
                        type: AliffShapeType.polygon,
                        points: expandedHolePolygon.map((point) => AliffHelpers.getPoint(point)),
                    };
                });
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                expandedHoleShapes.forEach((expandedHoleShape) => {
                    layers[layerOrder]?.keepouts.push({
                        type: keepoutType,
                        layer: layerOrder,
                        shape: expandedHoleShape,
                    });
                });
            }
        }
    }
    generateLayers() {
        const aliffLayers = this.copperLayers.map((layer, order) => {
            const layerId = layer.orientation === LayerOrientation.top
                ? "F.Cu"
                : layer.orientation === LayerOrientation.bottom
                    ? "B.Cu"
                    : `In${order}.Cu`;
            const type = layer.type === "Power Plane" ? "power" : "signal";
            if (type === "power") {
                this.exportTimeErrors.push({
                    code: AliffExportTimeErrorCode.POWER_LAYER_AVOIDED,
                    data: { layerId: getLayerIdentifier(layer) },
                });
            }
            return {
                id: layerId,
                type,
                keepouts: [],
            };
        });
        return aliffLayers;
    }
    getAliffNetId(netNodeOrUid) {
        if (!netNodeOrUid) {
            return AliffExporter.UNCONNECTED_NET_ID;
        }
        const netNode = typeof netNodeOrUid === "string" ? this.getBakedNode(netNodeOrUid, PcbNodeTypes.net) : netNodeOrUid;
        if (!netNode) {
            return AliffExporter.UNCONNECTED_NET_ID;
        }
        let netId = this.netNodeToAliffNetIdMap[netNode.uid];
        if (!netId) {
            netId = AliffHelpers.getUniqueNodeName(netNode, false);
            this.netNodeToAliffNetIdMap[netNode.uid] = netId;
            this.objectIdToNodeUidMap[netId] = netNode.uid;
        }
        return netId;
    }
    generateNets() {
        const nets = {};
        const documentNets = Object.values(this.documentData.nets);
        // Collect all net priorities for transformation
        const netPriorities = new Map();
        for (const net of documentNets) {
            const netNode = this.getBakedNode(net.uid, PcbNodeTypes.net);
            if (netNode) {
                netPriorities.set(net.uid, netNode.bakedRules.autoLayoutPriority);
            }
        }
        // Transform priorities using the new utility
        // This maps Flux priorities (lower = higher priority) to DeepPCB format:
        // 1. Maps unique float priorities to unique integers (preserving order)
        // 2. Applies exponential spacing (~2.5x factor) for better routing
        // 3. Inverts values so lower Flux priority => higher DeepPCB priority
        const transformedPriorities = AliffPriorityMapper.transformPrioritiesForDeepPcb(netPriorities);
        // Add the additionalNets to the nets list. These are pin-less nets that are needed to support
        // vias that are not connected to any net
        const unconnectedNet = {
            id: AliffExporter.UNCONNECTED_NET_ID,
            pins: [],
        };
        if (this.jobConfig.includeRoutingPriority === true) {
            unconnectedNet.routingPriority = 0;
        }
        nets[unconnectedNet.id] = unconnectedNet;
        if (netPriorities.size === 0) {
            return nets;
        }
        for (const net of documentNets) {
            const netNode = this.getBakedNode(net.uid, PcbNodeTypes.net);
            if (!netNode) {
                continue;
            }
            const pins = [];
            for (const pin of Object.values(net.pins ?? {})) {
                const elementNode = this.getBakedNode(pin.elementUid, PcbNodeTypes.element);
                const pinNode = this.getBakedNode(pin.uid, PcbNodeTypes.pad);
                if (!elementNode || !pinNode) {
                    continue;
                }
                const elementName = AliffHelpers.getUniqueNodeName(elementNode, true);
                const pinName = AliffHelpers.getUniqueNodeName(pinNode, true);
                // Prevent adding connections to a net for elements or pins that are not created
                if (!this.createdElementsCache.has(elementName)) {
                    continue;
                }
                if (!this.createdPinsCache.has(pinName)) {
                    continue;
                }
                pins.push(`${elementName}-${pinName}`);
            }
            // Push this net even if `pins` is empty, since the net might be used by vias/traces
            const netObject = {
                id: this.getAliffNetId(net.uid),
                pins,
            };
            // Set the transformed routing priority
            if (this.jobConfig.includeRoutingPriority === true) {
                const transformedPriority = transformedPriorities.get(net.uid);
                if (transformedPriority !== undefined) {
                    netObject.routingPriority = transformedPriority;
                }
            }
            nets[netObject.id] = netObject;
        }
        return nets;
    }
    generateDefaultNetClass(throughHoleViaConfigUids) {
        // Use the baked net node to extract track width and clearance values
        const pseudoBakedTraceNode = this.createPseudoBakedTraceNode(this.pseudoBakedNetNode, this.activePcbLayoutNodesWithPseudoNetNode);
        // Create a baked smart via node to extract the default via definition
        const pseudoBakedSmartViaNode = this.createPseudoBakedSmartViaNode(this.pseudoBakedNetNode, throughHoleViaConfigUids.join(","), this.activePcbLayoutNodesWithPseudoNetNode);
        const trackWidth = AliffHelpers.toNm(pseudoBakedTraceNode.bakedRules.size.x);
        const viaDefinition = pseudoBakedSmartViaNode.bakedRules.selectedViaConfigs[0]?.uid ?? DEFAULT_THROUGH_HOLE_VIA_UID;
        // This should never happen, but as a safety check
        if (!this.viaDefinitions.includes(viaDefinition)) {
            throw new Error("Default via definition not found in via definitions");
        }
        return {
            id: AliffExporter.DEFAULT_NET_CLASS_ID,
            nets: [],
            trackWidth,
            clearance: AliffExporter.DEFAULT_NET_CLEARANCE_VALUE,
            viaDefinition,
        };
    }
    generateNetClasses(aliffNetsMap, throughHoleViaConfigUids) {
        const defaultNetClass = this.generateDefaultNetClass(throughHoleViaConfigUids);
        const netClasses = {
            [defaultNetClass.id]: defaultNetClass,
        };
        const { viaConfigsMap } = getStackupLayersAndViaConfigs(this.layoutNode.bakedRules, this.activePcbLayoutNodes);
        const netUidToNetClassMap = {};
        const netNodes = this.getNodesOfType(PcbNodeTypes.net);
        for (const netNode of netNodes) {
            const aliffNetId = this.getAliffNetId(netNode);
            if (!aliffNetsMap[aliffNetId]) {
                continue;
            }
            const pseudoBakedTraceNode = this.createPseudoBakedTraceNode(netNode);
            const traceWidth = AliffHelpers.toNm(pseudoBakedTraceNode.bakedRules.size.x);
            // Get the keepout value of an arbitrary trace node on this net, against a arbitrary net node
            const keepout = AliffHelpers.toNm(calcKeepoutForNodes(pseudoBakedTraceNode, this.pseudoBakedNetNode, this.pcbSelectorMatcher));
            const clearance = keepout === 0 || keepout === Infinity ? AliffExporter.DEFAULT_NET_CLEARANCE_VALUE : keepout;
            // Add a prospective smart via node to check what the via options turn out to be
            const pseudoBakedSmartViaNode = this.createPseudoBakedSmartViaNode(netNode);
            // Remove any unsupported via options
            const viaConfigUids = pseudoBakedSmartViaNode.bakedRules.viaOptions;
            const supportedViaOptions = viaConfigUids.filter((uid) => throughHoleViaConfigUids.includes(uid));
            // Re-create the pseudo node with only the supported via options and pick the first one
            const [viaDefinition = DEFAULT_THROUGH_HOLE_VIA_UID] = getEnrichedViaOptions(supportedViaOptions, viaConfigsMap);
            // Otherwise, create a new net class
            const netClassId = aliffNetId;
            const netClass = netClasses[netClassId] ?? {
                id: netClassId,
                nets: [],
                trackWidth: traceWidth,
                clearance,
                viaDefinition,
            };
            netClass.nets.push(aliffNetId);
            netClasses[netClassId] = netClass;
            netUidToNetClassMap[netNode.uid] = netClass;
        }
        // Add `UNCONNECTED_NET_ID` to the default net class
        defaultNetClass.nets.push(AliffExporter.UNCONNECTED_NET_ID);
        return { netClasses: Object.values(netClasses), netUidToNetClassMap };
    }
    generateNetPreferences(aliffNetsMap) {
        if (!this.jobConfig.includeNetPreferences) {
            return [];
        }
        const netPreferences = [];
        const documentNets = Object.values(this.documentData.nets);
        for (const config of DEFAULT_NET_PREFERENCE_CONFIGS) {
            const matchingNetIds = [];
            for (const documentNet of documentNets) {
                const netNode = this.getBakedNode(documentNet.uid, PcbNodeTypes.net);
                if (!netNode) {
                    continue;
                }
                const aliffNetId = this.getAliffNetId(netNode);
                if (!aliffNetsMap[aliffNetId]) {
                    continue;
                }
                if (config.condition(documentNet)) {
                    matchingNetIds.push(aliffNetId);
                }
            }
            if (matchingNetIds.length > 0) {
                const netPreference = {
                    id: config.id,
                    nets: matchingNetIds,
                    ...config.coefficients,
                };
                netPreferences.push(netPreference);
            }
        }
        return netPreferences;
    }
    generateDifferentialPairs(netUidToNetMap, netUidToNetClassMap) {
        const differentialPairs = [];
        const multiRoutingGroups = getMultiRoutingGroups(this.documentData.nets, this.documentData.elements, this.activePcbLayoutNodes);
        for (const multiRoutingGroup of multiRoutingGroups) {
            // We only support pair-routing right now, so we assuming that there are only 2 nets in a group
            const [netUid1, netUid2] = multiRoutingGroup.netUids;
            if (!netUid1 || !netUid2)
                continue;
            const netClass1 = netUidToNetClassMap[netUid1];
            const netClass2 = netUidToNetClassMap[netUid2];
            if (!netClass1 || !netClass2)
                continue;
            const net1 = netUidToNetMap[netClass1.id];
            const net2 = netUidToNetMap[netClass2.id];
            if (!net1 || !net2)
                continue;
            // Skip this group if the nets are not of the same length, otherwise DeepPCB complains
            if (net1.pins.length !== net2.pins.length) {
                this.exportTimeErrors.push({
                    code: AliffExportTimeErrorCode.DIFFERENTIAL_PAIRS_PINS_MISMATCH,
                    data: {
                        nets: [netUid1, netUid2],
                    },
                });
                continue;
            }
            const clearance = Math.min(netClass1.clearance, netClass2.clearance);
            const trackWidth = Math.min(netClass1.trackWidth, netClass2.trackWidth);
            differentialPairs.push({
                netId1: this.getAliffNetId(netUid1),
                netId2: this.getAliffNetId(netUid2),
                gap: clearance,
                trackWidth,
            });
        }
        return differentialPairs;
    }
    generatePlanes(boardBoundary) {
        const data = { planes: [], nodesWithFills: [] };
        // Use the fill primitives map to get the nets with fills
        const fillShapesMap = this.services.pcbPrimitiveStore.getFillShapesMap();
        if (!fillShapesMap) {
            return data;
        }
        data.nodesWithFills = Object.keys(fillShapesMap)
            .map((uid) => this.activePcbLayoutNodes[uid])
            .filter((node) => node?.type === PcbNodeTypes.net || node?.type === PcbNodeTypes.polygon);
        if (data.nodesWithFills.length === 0) {
            return data;
        }
        let maxClearanceFromLayoutEdge = 0;
        for (const nodeWithFill of data.nodesWithFills) {
            // Skip if the fill node is disabled
            const [fillNode] = this.getNodesOfType(PcbNodeTypes.fill, nodeWithFill);
            if (!fillNode?.bakedRules.active)
                continue;
            const netNodeUid = fillNode.bakedRules.effectiveNetId;
            if (!netNodeUid)
                continue;
            const boundary = nodeWithFill.type === PcbNodeTypes.net ? boardBoundary : this.generatePolygonBoundary(nodeWithFill);
            const polygonProtectionData = {};
            if (this.jobConfig.protectPolygons && nodeWithFill.type === PcbNodeTypes.polygon) {
                const shouldProtectPolygon = nodeWithFill.bakedRules.protected ?? false;
                if (this.jobConfig.useKeepoutRuleForPolygonProtection) {
                    polygonProtectionData.keepoutRule = shouldProtectPolygon ? ["wire", "via"] : undefined;
                }
                else {
                    polygonProtectionData.protected = shouldProtectPolygon;
                }
                if (shouldProtectPolygon) {
                    this.exportTimeErrors.push({
                        code: AliffExportTimeErrorCode.POLYGON_PROTECTED,
                        data: {
                            polygonNodeUid: nodeWithFill.uid,
                        },
                    });
                }
            }
            const connectedLayers = getConnectedCopperLayers(nodeWithFill, this.copperLayers);
            for (const layer of connectedLayers) {
                data.planes.push({
                    netId: this.getAliffNetId(netNodeUid),
                    layer: this.getLayerOrder(layer),
                    // Don't send any hole data, just send the board/polygon outline
                    // The board.boundary.clearance will take care of the spacing from the board edge
                    // We're doing this because DeepPCB claims better results for when
                    // our supplied plane data is just the outline, and they interpret the holes
                    shape: {
                        type: AliffShapeType.polygonWithHoles,
                        outline: boundary.shape.points,
                        holes: [],
                    },
                    ...polygonProtectionData,
                });
            }
            // TODO: clearance for polygons
            // Calculated keepout from the net node to the layout node and update maxClearance
            const netClearanceFromLayoutEdge = this.layoutNode.bakedRules.boardInsetMargin ?? 0;
            maxClearanceFromLayoutEdge = Math.max(maxClearanceFromLayoutEdge, netClearanceFromLayoutEdge);
        }
        // Update the board clearance with the new maxClearance value
        boardBoundary.clearance = AliffHelpers.toNm(maxClearanceFromLayoutEdge);
        return data;
    }
    generateWires() {
        const layoutPosition = this.layoutNode.bakedRules.positionRelativeToDocument;
        const layoutRotation = this.layoutNode.bakedRules.rotationRelativeToDocument.z;
        const traceNodes = this.getNodesOfType(PcbNodeTypes.routeSegment);
        return traceNodes.flatMap((traceNode) => {
            const netId = this.getAliffNetId(traceNode.bakedRules.effectiveNetId);
            const layer = this.getLayerOrder(traceNode.bakedRules.layer);
            const width = AliffHelpers.toNm(traceNode.bakedRules.size.x);
            const type = "segment";
            const userData = JSON.stringify({ traceNodeUid: traceNode.uid });
            const traceParentNode = this.getBakedNode(traceNode.parentUid, PcbNodeTypes.net);
            if (!traceParentNode) {
                return [];
            }
            const traceNodeStart = TraceCorners.computeForVertex("start", traceNode, this.activePcbLayoutNodes, this.services.pcbTracingGraph.getGraph());
            const traceNodeEnd = TraceCorners.computeForVertex("end", traceNode, this.activePcbLayoutNodes, this.services.pcbTracingGraph.getGraph());
            const traceStart = traceNodeStart?.vertex ?? traceNode.bakedRules.startPosition;
            const traceEnd = traceNodeEnd?.vertex ?? traceNode.bakedRules.endPosition;
            const traceVectorStart = getNodeAbsVertexPosition(traceStart, traceNode, traceParentNode);
            const traceVectorEnd = getNodeAbsVertexPosition(traceEnd, traceNode, traceParentNode);
            const startVector = vec2.rotateAround(traceVectorStart, layoutPosition, -layoutRotation);
            const endVector = vec2.rotateAround(traceVectorEnd, layoutPosition, -layoutRotation);
            const start = AliffHelpers.getPoint(startVector, layoutPosition);
            const end = AliffHelpers.getPoint(endVector, layoutPosition);
            const segments = [
                {
                    type,
                    netId,
                    layer,
                    start,
                    end,
                    width,
                    protected: true,
                    userData,
                },
            ];
            // Iterate corner on start vertex of trace, if present, and append segments to serialization
            for (const segment of TraceCorners.segmentGenerator(traceNodeStart?.arc)) {
                const traceVectorStart = getNodeAbsVertexPosition({ ...segment.start, z: 0 }, traceNode, traceParentNode);
                const traceVectorEnd = getNodeAbsVertexPosition({ ...segment.end, z: 0 }, traceNode, traceParentNode);
                const start = AliffHelpers.getPoint(vec2.rotateAround(traceVectorStart, layoutPosition, -layoutRotation), layoutPosition);
                const end = AliffHelpers.getPoint(vec2.rotateAround(traceVectorEnd, layoutPosition, -layoutRotation), layoutPosition);
                segments.push({
                    type,
                    netId,
                    layer,
                    start,
                    end,
                    width,
                    protected: true,
                    userData,
                });
            }
            // Iterate corner on end vertex of trace, if present, and append segments to serialization
            for (const segment of TraceCorners.segmentGenerator(traceNodeEnd?.arc)) {
                const traceVectorStart = getNodeAbsVertexPosition({ ...segment.start, z: 0 }, traceNode, traceParentNode);
                const traceVectorEnd = getNodeAbsVertexPosition({ ...segment.end, z: 0 }, traceNode, traceParentNode);
                const start = AliffHelpers.getPoint(vec2.rotateAround(traceVectorStart, layoutPosition, -layoutRotation), layoutPosition);
                const end = AliffHelpers.getPoint(vec2.rotateAround(traceVectorEnd, layoutPosition, -layoutRotation), layoutPosition);
                segments.push({
                    type,
                    netId,
                    layer,
                    start,
                    end,
                    width,
                    protected: true,
                    userData,
                });
            }
            return segments;
        });
    }
    generatePadStacksForAllViaConfigs() {
        const throughHoleViaConfigUids = [];
        const { viaConfigs, viaHoleConfigs } = getStackupLayersAndViaConfigs(this.layoutNode.bakedRules, this.activePcbLayoutNodes);
        for (const viaConfig of viaConfigs) {
            const unsupportViaConfigExportTimeError = {
                code: AliffExportTimeErrorCode.UNSUPPORTED_VIA_CONFIG,
                data: { viaConfig },
            };
            if (!viaConfig.enabled) {
                this.exportTimeErrors.push(unsupportViaConfigExportTimeError);
                continue;
            }
            // We only want to care about via configs connect to all copper layers
            // This can be either one big throughHole config, or a combination of buried/blind/micro
            // vias that connect all layers
            const viaConfigConnectedLayers = getViaConfigConnectedLayers(viaConfig, this.copperLayers);
            if (viaConfigConnectedLayers.length < this.copperLayers.length) {
                this.exportTimeErrors.push(unsupportViaConfigExportTimeError);
                continue;
            }
            // We choose the largest size from all segments in the via config to send to deeppcb
            const maxSize = viaConfig.segments.reduce((acc, segment) => {
                const resolvedSegmentRuleSet = getViaSegmentResolvedSizeValues(segment, viaHoleConfigs, "mm");
                const pseudoBakedViaNode = this.createPseudoBakedViaNode(resolvedSegmentRuleSet);
                const size = pseudoBakedViaNode.bakedRules.size.x;
                return Math.max(acc, size);
            }, 0);
            if (maxSize <= 0) {
                this.exportTimeErrors.push(unsupportViaConfigExportTimeError);
                continue;
            }
            const padStackId = viaConfig.uid;
            const layerOrders = this.copperLayers.map((layer) => this.getLayerOrder(layer));
            const layerFrom = layerOrders.at(0);
            const layerTo = layerOrders.at(-1);
            if (layerFrom === undefined || layerTo === undefined) {
                this.exportTimeErrors.push(unsupportViaConfigExportTimeError);
                continue;
            }
            if (isThroughHoleViaConfig(viaConfig, this.copperLayers)) {
                throughHoleViaConfigUids.push(padStackId);
            }
            this.viaDefinitions.push(padStackId);
            this.padStacks[padStackId] ??= {
                id: padStackId,
                pads: [
                    {
                        shape: {
                            type: AliffShapeType.circle,
                            center: AliffHelpers.getPoint(AliffHelpers.origin),
                            radius: AliffHelpers.toNm(maxSize / 2),
                        },
                        layerFrom,
                        layerTo,
                    },
                ],
                allowVia: true,
            };
        }
        return { throughHoleViaConfigUids };
    }
    generatePadstackForViaNode(viaNode) {
        let segmentInfo;
        if (PcbNodeHelper.isNodeUnderSmartVia(viaNode)) {
            const smartViaParentNode = this.getBakedNode(viaNode.parentUid, PcbNodeTypes.smartVia);
            if (smartViaParentNode) {
                segmentInfo = getViaSegmentFromType(viaNode.bakedRules.viaType, smartViaParentNode.bakedRules.selectedViaConfigs);
            }
        }
        // Don't create vias/via pad stacks for stitching vias
        if (PcbNodeHelper.isVirtualNode(viaNode) && viaNode.virtualNodeInfo.originRule === "fill") {
            return undefined;
        }
        // Now we need to check whether this via is a part of a smartVia supported config
        // if yes: we need to check whether the baked size and layers of the node match that of the config
        //         in cases where their size or layers has been changed by an object-specific or important rule
        //         if the match: we'll use the config's uid as the padstackId
        //         if not a match, we'll create a new padstack for it
        // if no:  create or use an existing padstack for the via
        // DeepPCB only supports mechanically-drilled vias right now (through-hole, blind, buried),
        // so we should not consider the smart via's connectivity to determine this segment's connectivity,
        // since mechanically drilled vias can connect to all layers it passes through.
        const connectedLayers = getConnectedCopperLayers(viaNode, this.copperLayers);
        const layerOrders = connectedLayers.map((layer) => this.getLayerOrder(layer));
        const viaScaledSize = vec2.scale(viaNode.bakedRules.size, viaNode.bakedRules.scale);
        const viaScaledRadius = viaScaledSize.x / 2;
        // If the via is part of a smartVia supported config, we'll use the config's uid as the padstackId
        if (segmentInfo?.viaConfig) {
            const viaConfigUid = segmentInfo.viaConfig.uid;
            const padstackForViaConfig = this.padStacks[viaConfigUid];
            if (padstackForViaConfig) {
                const layersMatch = "layers" in padstackForViaConfig
                    ? padstackForViaConfig.layers.every((layerOrder) => layerOrders.includes(layerOrder))
                    : false;
                const radiiMatch = "shape" in padstackForViaConfig
                    ? padstackForViaConfig.shape.radius ===
                        AliffHelpers.toNm(viaScaledRadius)
                    : false;
                if (layersMatch && radiiMatch) {
                    return padstackForViaConfig;
                }
            }
        }
        // The padstackId will be the same for vias with the same layers and radius
        const padstackId = AliffHelpers.hash([layerOrders, viaScaledRadius]);
        // Don't create a duplicate padstack if it already exists. Return the existing one
        const existingPadstack = this.padStacks[padstackId];
        if (existingPadstack) {
            return existingPadstack;
        }
        const layerFrom = layerOrders.at(0);
        const layerTo = layerOrders.at(-1);
        if (layerFrom === undefined || layerTo === undefined) {
            return undefined;
        }
        const padstack = {
            id: padstackId,
            allowVia: true,
            pads: [
                {
                    shape: {
                        type: AliffShapeType.circle,
                        center: AliffHelpers.getPoint(AliffHelpers.origin),
                        radius: AliffHelpers.toNm(viaScaledRadius),
                    },
                    layerFrom,
                    layerTo,
                },
            ],
        };
        this.padStacks[padstack.id] = padstack;
        this.viaDefinitions.push(padstack.id);
        return padstack;
    }
    generateStandardShapeFromPrimitive(primitive) {
        if (primitive.type === PrimitiveType.Circle) {
            return {
                type: AliffShapeType.circle,
                center: AliffHelpers.getPoint(AliffHelpers.origin),
                radius: AliffHelpers.toNm(primitive.radius),
            };
        }
        if (primitive.type === PrimitiveType.Rectangle) {
            const halfWidth = primitive.width / 2;
            const halfHeight = primitive.height / 2;
            return {
                type: AliffShapeType.rectangle,
                lowerLeft: AliffHelpers.getPoint([-halfWidth, -halfHeight]),
                upperRight: AliffHelpers.getPoint([halfWidth, halfHeight]),
            };
        }
        // Since we only care about the outline and not the hole, we can further evaluate on the first Difference primtive
        if (primitive.type === PrimitiveType.Difference) {
            return this.generateStandardShapeFromPrimitive(primitive.primitiveA);
        }
        // Rest shapes are non-standard per DeepPCB, so we return undefined
        return undefined;
    }
    generatePadShapeOutline(padNode, primitives) {
        // We only care about the first copper shape, since other copper shapes
        // have the same shape, they're just on a different layer
        const copperPrimitive = primitives.find((shape) => shape.role === PrimitiveRole.copper);
        // If there's no copper primitive, we'll create a keepout for the pad
        if (!copperPrimitive) {
            return undefined;
        }
        // First check if the primitive can be exported as a standard shape (circle or rectangle)
        // Note: since we only care about the outline and not the holes, the Difference primitive can also
        // be exported as just a circle or square (if possible)
        const standardShape = this.generateStandardShapeFromPrimitive(copperPrimitive);
        if (standardShape) {
            return standardShape;
        }
        // For rest of the shapes, compute the polygon
        const padPosition = padNode.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0 };
        const padRotation = padNode.bakedRules.rotationRelativeToRootLayout?.z ?? 0;
        // We want to "undo" the rotation for the pad because we want to send the pad in its original orientation
        // and let DeepPCB apply the rotation on their end when it is referenced in a component
        // See: type `AliffPin` and method `createAliffPinFromPadNode` for where the rotation is applied
        const adjustedRotation = -padRotation;
        return {
            type: AliffShapeType.polygon,
            points: approxFittingPolygon(copperPrimitive, CurveDivisions).map((point) => {
                // Rotate the point around the pad's position to get the point in the pad's original orientation
                const rotatedPoint = vec2.rotateAround(point, padPosition, adjustedRotation);
                return AliffHelpers.getPoint(rotatedPoint, padPosition);
            }),
        };
    }
    generatePadHoleShape(padNode, primitives) {
        // We're using the primitives to check if the pad has a meaningful hole
        // and using the padNode's baked rules to get the hole dimensions
        const padHasMeaningfulHole = primitives.some((primitive) => {
            if (primitive.role === PrimitiveRole.layoutHole) {
                const polygon = approxFittingPolygon(primitive, CurveDivisions);
                const clipperShape = Clipper.shape([polygon]);
                // `totalArea()` can be positive or negative, depending on the winding order of the shape
                // We want to consider the shape only if it has a non-zero area
                return clipperShape.totalArea() !== 0;
            }
            return false;
        });
        if (!padHasMeaningfulHole) {
            return [undefined, false];
        }
        const size = padNode.bakedRules.size;
        const scale = padNode.bakedRules.scale;
        const scaledSize = vec2.scale(size, scale);
        const holeSize = padNode.bakedRules.hole?.holeSize;
        // Create keepout for hole pads with hole size >= pad size
        if (!holeSize || (holeSize.x >= scaledSize.x && holeSize.y >= scaledSize.y)) {
            return [undefined, true];
        }
        // Ready the hole shape data with placeholder values
        const holeShape = {
            type: AliffShapeType.path,
            width: 0,
            points: [],
        };
        //
        // Circular hole - can be defined with a single point with width as radius
        //
        if (holeSize.x === holeSize.y) {
            holeShape.width = AliffHelpers.toNm(holeSize.x); // Radius
            holeShape.points = [AliffHelpers.getPoint(AliffHelpers.origin)];
        }
        //
        // Oblong hole - defined with a line (two points) with width as radius
        //
        // The width expands the hole line to make it an oblong shape. Thus the _resolved_ length
        // is the length of the line minus the width because the width expands in both directions
        //
        else {
            // We need to give the line the pad's rotation to align it correctly
            const getPoint = (x, y) => vec2
                .chain({ x, y })
                .rotateAround(AliffHelpers.origin, padNode.bakedRules.rotationRelativeToRootLayout?.z ?? 0)
                .tuple();
            let width;
            let points;
            //
            // Something like this:
            // +---------------+
            // | +-----------+ |
            // | |           | |
            // | +-----------+ |
            // +---------------+
            //
            if (holeSize.x > holeSize.y) {
                width = AliffHelpers.toNm(holeSize.y);
                const length = AliffHelpers.toNm(holeSize.x);
                const resolvedLength = length - width;
                const startPoint = getPoint(-resolvedLength / 2, 0);
                const endPoint = getPoint(resolvedLength / 2, 0);
                points = [startPoint, endPoint];
            }
            //
            // Something like this:
            // +-------+
            // | +---+ |
            // | |   | |
            // | |   | |
            // | |   | |
            // | |   | |
            // | +---+ |
            // +-------+
            //
            else {
                width = AliffHelpers.toNm(holeSize.x);
                const length = AliffHelpers.toNm(holeSize.y);
                const resolvedLength = length - width;
                const startPoint = getPoint(0, -resolvedLength / 2);
                const endPoint = getPoint(0, resolvedLength / 2);
                points = [startPoint, endPoint];
            }
            holeShape.width = width;
            holeShape.points = points;
        }
        return [holeShape, false];
    }
    generatePadShape(padNode) {
        const padShapeFromShapeStore = this.nodeShapesMap.get(padNode.uid);
        const primitives = padShapeFromShapeStore?.primitives;
        if (!primitives) {
            return undefined;
        }
        const outlineShape = this.generatePadShapeOutline(padNode, primitives);
        if (!outlineShape) {
            this.holeTypePadNodes.push(padNode);
            return undefined;
        }
        const [holeShape, createKeepoutForHole] = this.generatePadHoleShape(padNode, primitives);
        if (createKeepoutForHole) {
            this.holeTypePadNodes.push(padNode);
        }
        return {
            outline: outlineShape,
            hole: holeShape,
        };
    }
    generatePadstackForPadNode(padNode) {
        const connectedLayers = getConnectedCopperLayers(padNode, this.copperLayers);
        const layerIds = connectedLayers.map((layer) => this.getLayerOrder(layer));
        if (layerIds.length === 0) {
            return undefined;
        }
        const padShapeData = this.generatePadShape(padNode);
        if (!padShapeData) {
            return undefined;
        }
        // Allow only STD/platedThroughHole type pads to have vias
        const padType = padNode.bakedRules.hole?.holeType;
        const allowViaPadTypes = [FootPrintPadHoleType.platedThroughHole];
        const allowVia = padType ? allowViaPadTypes.includes(padType) : false;
        // The padstackId will be the same for pads with the same layerIds, shape, and allowVia
        // This is achieved by hashing the above values and using the hash as the padstackId
        const padstackId = AliffHelpers.hash([layerIds, padShapeData, allowVia]);
        // Don't create a duplicate padstack if it already exists. Return the existing one
        // This reduces the file size by a lot (e.g. 4mb -> 925kb on a medium-complexity board),
        // but unfortunately we _need_ to do the heavy computation of generating padShapes
        // for all pad nodes to enable this optimization
        const existingPadstack = this.padStacks[padstackId];
        if (existingPadstack) {
            return existingPadstack;
        }
        const layerFrom = layerIds.at(0);
        const layerTo = layerIds.at(-1);
        if (layerFrom === undefined || layerTo === undefined) {
            return undefined;
        }
        let padstack;
        if (padShapeData.hole) {
            // AliffPadstackAdvancedWithHole
            padstack = {
                id: padstackId,
                pads: [{ shape: padShapeData.outline, layerFrom, layerTo }],
                hole: { shape: padShapeData.hole },
                layers: layerIds,
                allowVia,
            };
        }
        else {
            // AliffPadstackAdvanced
            padstack = {
                id: padstackId,
                pads: [{ shape: padShapeData.outline, layerFrom, layerTo }],
                allowVia,
            };
        }
        this.padStacks[padstackId] = padstack;
        return padstack;
    }
    createAliffPinFromPadNode(padNode, parentNode) {
        // Create keepout for non-plated holes, not pins
        if (padNode.bakedRules.hole?.holeType === FootPrintPadHoleType.nonPlatedHole) {
            this.holeTypePadNodes.push(padNode);
            return undefined;
        }
        const padstack = this.generatePadstackForPadNode(padNode);
        if (!padstack) {
            return undefined;
        }
        // We want the relative position of the _rotated_ pad w.r.t the parent element
        // The offset is the parent element's position, so we subtract it from the pad's position
        // This must be supplied correctly by the caller
        const parentPosition = parentNode.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0 };
        const padPosition = padNode.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0 };
        const position = vec2.sub(padPosition, parentPosition);
        // Rotation is the absolute rotation of the pad (relative to layout), and not relative to element
        // since we're specifying the rotation of the element to be 0
        const rotation = padNode.bakedRules.rotationRelativeToRootLayout?.z ?? 0;
        const pinName = AliffHelpers.getUniqueNodeName(padNode, true);
        return {
            id: pinName,
            padstack: padstack.id,
            // Relative position of the rotated pad w.r.t the parent element
            position: AliffHelpers.getPoint(position),
            rotation: AliffHelpers.getRotation(rotation),
        };
    }
    createComponentDefinitionAndComponent(node, pins) {
        const componentId = AliffHelpers.getUniqueNodeName(node, true);
        const componentDefinition = {
            id: componentId,
            pins,
            keepouts: [],
            // For now, we don't have a reliable way to compute the outline of a component
            outline: undefined,
        };
        const position = node.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0 };
        const component = {
            definition: componentId,
            id: componentId,
            partNumber: node.name,
            side: "FRONT",
            position: AliffHelpers.getPoint(position),
            rotation: 0,
        };
        this.createdElementsCache.add(componentId);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        pins.forEach((pin) => this.createdPinsCache.add(pin.id));
        return { componentDefinition, component };
    }
    generateComponents() {
        const componentDefinitions = [];
        const components = [];
        const elementNodes = getAllLeafElementNodes(this.activePcbLayoutNodes, this.layoutNode.uid);
        for (const elementNode of elementNodes) {
            // Create pins from pad nodes, and unique padStacks for all pad nodes to prevent ambiguity
            const padNodes = this.getNodesOfType(PcbNodeTypes.pad, elementNode);
            const pins = [];
            const componentId = AliffHelpers.getUniqueNodeName(elementNode, true);
            for (const padNode of padNodes) {
                const pin = this.createAliffPinFromPadNode(padNode, elementNode);
                if (!pin)
                    continue;
                pins.push(pin);
                this.objectIdToNodeUidMap[pin.id] = padNode.uid;
                this.objectIdToNodeUidMap[`${componentId}-${pin.id}`] = padNode.uid;
            }
            const { componentDefinition, component } = this.createComponentDefinitionAndComponent(elementNode, pins);
            this.objectIdToNodeUidMap[componentId] = elementNode.uid;
            componentDefinitions.push(componentDefinition);
            components.push(component);
        }
        // For pad nodes that do not have a parent/top-level element node, create a dummy
        // component definition and component to represent them
        // We get non-element pad nodes by excluding element nodes from the list of all pad nodes
        const nonElementPadNodes = this.getNodesOfType(PcbNodeTypes.pad, undefined, elementNodes).filter((node) => !node.terminalId);
        for (const padNode of nonElementPadNodes) {
            const pin = this.createAliffPinFromPadNode(padNode, padNode); // Parent node is the pad node itself
            if (!pin)
                continue;
            const pins = [pin];
            const { componentDefinition, component } = this.createComponentDefinitionAndComponent(padNode, pins);
            // Add all variants to the map
            this.objectIdToNodeUidMap[pin.id] = padNode.uid;
            this.objectIdToNodeUidMap[`${component.id}-${pin.id}`] = padNode.uid;
            this.objectIdToNodeUidMap[component.id] = padNode.uid;
            componentDefinitions.push(componentDefinition);
            components.push(component);
        }
        return {
            componentDefinitions,
            components,
        };
    }
    generateVias() {
        const vias = [];
        const viaNodes = this.getNodesOfType(PcbNodeTypes.via);
        for (const viaNode of viaNodes) {
            const padstack = this.generatePadstackForViaNode(viaNode);
            if (!padstack)
                continue;
            vias.push({
                netId: this.getAliffNetId(viaNode.bakedRules.effectiveNetId),
                padstack: padstack.id,
                position: AliffHelpers.getPoint(viaNode.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0 }),
                protected: true,
                userData: JSON.stringify({ viaNodeUid: viaNode.uid }),
            });
        }
        return vias;
    }
    createPseudoBakedSmartViaNode(netNode, viaConfigUids, pcbLayoutNodes = this.activePcbLayoutNodes) {
        const rules = { connectedLayers: "All" };
        if (viaConfigUids) {
            rules.viaOptions = viaConfigUids;
        }
        const newSmartViaNode = createNode(PcbNodeTypes.smartVia, "DummySmartVia", {
            uid: guid(),
            parentUid: netNode.uid,
        }, rules);
        return createBakedPseudoNode(newSmartViaNode, this.documentData, pcbLayoutNodes, {
            dangerouslyMutateInPlace: true,
        });
    }
    createPseudoBakedTraceNode(netNode, pcbLayoutNodes = this.activePcbLayoutNodes) {
        const newTraceNode = createRouteSegmentNode(guid(), guid(), new Vector3(), new Vector3(), netNode.uid, LayerOrientation.top);
        return createBakedPseudoNode(newTraceNode, this.documentData, pcbLayoutNodes, {
            dangerouslyMutateInPlace: true,
        });
    }
    createPseudoBakedNetNode() {
        const newNetNode = createNetNode(guid(), this.layoutNode.uid, "DummyNet", { topLevel: [], submodule: [] });
        return createBakedPseudoNode(newNetNode, this.documentData, this.activePcbLayoutNodes, {
            dangerouslyMutateInPlace: true,
        });
    }
    createPseudoBakedViaNode(rules, parentUid = this.layoutNode.uid) {
        const newViaNode = createNode(PcbNodeTypes.via, "DummyVia", { uid: guid(), parentUid }, rules);
        return createBakedPseudoNode(newViaNode, this.documentData, this.activePcbLayoutNodes, {
            dangerouslyMutateInPlace: true,
        });
    }
    getNodesOfType(nodeType, rootNode = this.layoutNode, ignoreNodes = []) {
        const ignoreNodeUids = ignoreNodes.map((node) => (typeof node === "string" ? node : node.uid));
        return getDescendantsOfType(this.activePcbLayoutNodes, rootNode, nodeType, ignoreNodeUids);
    }
    getBakedNode(nodeUid, nodeType) {
        const node = this.activePcbLayoutNodes[nodeUid];
        if (node?.type !== nodeType) {
            return undefined;
        }
        return node;
    }
}
//# sourceMappingURL=AliffExporter.js.map